import { escape, unescape } from 'lodash';

import { getCmlAttribute } from 'bundles/cml/editor/utils/cmlUtils';
import { sanitizeURL } from 'bundles/cml/editor/utils/sanitizeURL';
import { MATH_REGEX } from 'bundles/cml/legacy/models/CMLParser';
import { BLOCK_TYPES } from 'bundles/cml/shared/constants';
import type {
  AssetElement,
  BlockElement,
  CodeElement,
  FillableBlankElement,
  HeadingElement,
  ImageElement,
  InlineElement,
  LegacyAudioElement,
  LinkElement,
  ListItemElement,
  MathBlockElement,
  MathInlineElement,
  PersonalizationTagElement,
  TableElement,
  TableRowElement,
  Text,
  TextElement,
  WidgetElement,
} from 'bundles/cml/shared/types/elementTypes';

export const MARKS_TO_CML = {
  bold: 'strong',
  italic: 'em',
  underline: 'u',
  monospace: 'var',
  superscript: 'sup',
  subscript: 'sub',
} as const;

const MARKS = Object.keys(MARKS_TO_CML) as (keyof typeof MARKS_TO_CML)[];

export const getTextContent = (node: Text | undefined) => {
  let result = escape(node?.text || '');
  MARKS.forEach((mark: keyof typeof MARKS_TO_CML) => {
    if (node?.[mark] === true) {
      const tag = MARKS_TO_CML[mark];
      result = `<${tag}>${result}</${tag}>`;
    }
  });

  return result;
};

// returns cml text string for a node
export const getTextCML = (node: BlockElement | InlineElement | Text) => {
  if (node.hasOwnProperty('text') || !node.hasOwnProperty('children')) {
    const textElement = node as Text;
    const textCML = getTextContent(textElement);
    const hasMath = !!textElement?.text?.match(MATH_REGEX);

    return {
      textCML,
      attributes: {
        hasMath,
      },
    };
  }

  const textNodes: Array<string> = [];
  let hasMath = false;

  (node as { children: (InlineElement | Text)[] }).children.forEach((childNode: InlineElement | Text) => {
    if (childNode.hasOwnProperty('text')) {
      const textElement = childNode as Text;
      const textContent = getTextContent(textElement);
      hasMath = hasMath || !!textElement.text?.match(MATH_REGEX);

      textNodes.push(textContent);
      return;
    }

    const inlineElement = childNode as InlineElement;
    if (inlineElement.type === 'link') {
      const linkElement = inlineElement as LinkElement;
      const href = sanitizeURL(linkElement.href);
      const linkTitle = escape(linkElement.title) || '';
      textNodes.push(
        `<a href="${href}" title="${linkTitle}">${linkElement.children
          .map((n: Text | MathInlineElement) => {
            const result = getTextCML(n);
            hasMath = hasMath || result.attributes.hasMath;
            return result.textCML;
          })
          .join('')}</a>`
      );
    } else if (inlineElement.type === BLOCK_TYPES.PERSONALIZATION_TAG) {
      const personalizationElement = inlineElement as PersonalizationTagElement;
      if (personalizationElement.tagValue) {
        textNodes.push(`<ptag>${personalizationElement.tagValue}</ptag>`);
      }
    } else if (inlineElement.type === BLOCK_TYPES.FILLABLE_BLANK) {
      const fillableBlankElement = inlineElement as FillableBlankElement;
      const { uuid, responseType, innerText } = fillableBlankElement;
      const cmlAttributes = [
        ...getCmlAttribute(['uuid', uuid], !!uuid),
        ...getCmlAttribute(['type', responseType], !!responseType),
      ].join(' ');

      textNodes.push(`<fillable-blank ${cmlAttributes}>${innerText}</fillable-blank>`);
    } else if (inlineElement.type === BLOCK_TYPES.MATH_INLINE) {
      const mathInlineElement = inlineElement as MathInlineElement;
      const { formula } = mathInlineElement;
      if (formula) {
        hasMath = !!formula.match(MATH_REGEX);
        textNodes.push(escape(formula));
      }
    }
  });

  // TODO: just use xml-js to convert back to cml string?
  return {
    textCML: textNodes.join(''),
    attributes: {
      hasMath,
    },
  };
};

// recursively convert a list of slate Nodes to cml string content (i.e. content string inside `<co-content> ... </co-content>`)
export const slateToCml = (nodes?: (BlockElement | InlineElement)[]) => {
  if (!nodes || nodes.length === 0) {
    return '';
  }

  const cml: Array<string> = [];
  nodes.forEach((node) => {
    const { textCML, attributes } = getTextCML(node);

    // `Text` type node is handled in the default case, assume `SlateBlock | SlateInline` otherwise
    switch (node.type) {
      case BLOCK_TYPES.HEADING: {
        const { level, variant } = node as HeadingElement;

        const cmlAttributes = [
          ...getCmlAttribute(['level', level]),
          ...getCmlAttribute(['hasMath', 'true'], attributes.hasMath),
          ...getCmlAttribute(['variant', variant], !!variant),
        ].join(' ');

        cml.push(`<heading ${cmlAttributes}>${textCML}</heading>`);
        break;
      }

      case BLOCK_TYPES.BULLET_LIST:
      case BLOCK_TYPES.NUMBER_LIST: {
        const bulletType = node.type === BLOCK_TYPES.NUMBER_LIST ? 'numbers' : 'bullets';
        cml.push(`<list bulletType="${bulletType}">${slateToCml(node.children)}</list>`);
        break;
      }

      case BLOCK_TYPES.LIST_ITEM: {
        const {
          'aria-level': ariaLevel,
          'data-aria-level': dataAriaLevel,
          'aria-posinset': ariaPosinset,
          'data-aria-posinset': dataAriaPosinset,
        } = node as ListItemElement;

        const listLevel = ariaLevel || dataAriaLevel || '';
        const listPosinset = ariaPosinset || dataAriaPosinset || '';

        const cmlAttributes = [
          ...getCmlAttribute(['aria-level', listLevel], !!listLevel),
          ...getCmlAttribute(['aria-posinset', listPosinset], !!listPosinset),
        ].join(' ');

        cml.push(`<li${cmlAttributes.trim().length > 0 ? ` ${cmlAttributes}` : ''}>${slateToCml(node.children)}</li>`);
        break;
      }

      case BLOCK_TYPES.IMAGE: {
        const { id, src, size } = node as ImageElement;
        const cmlAttributes = [
          ...getCmlAttribute(['assetId', id], !!id),
          ...getCmlAttribute(['size', size], !!size && size !== 'default'),
          ...getCmlAttribute(['src', src], !id && !!src), // spark images use `src` and don't have `assetId`
        ].join(' ');

        cml.push(`<img ${cmlAttributes}/>`);
        break;
      }

      case BLOCK_TYPES.LEGACY_AUDIO: {
        const { id, name, src } = node as LegacyAudioElement;
        const cmlAttributes = [
          ...getCmlAttribute(['assetId', id], !!id),
          ...getCmlAttribute(['caption', name], !!name),
          ...getCmlAttribute(['src', src], !!src),
        ].join(' ');

        cml.push(`<audio ${cmlAttributes} />`);
        break;
      }

      case BLOCK_TYPES.ASSET: {
        const { id, name, extension, assetType, embedEnabled, embedStartPage, embedEndPage } = node as AssetElement;
        const escapedName = escape(unescape(name)); // unescape first so we don't doubly escape

        const embedStartAttr = embedStartPage ? ` embedStartPage="${embedStartPage}"` : '';
        const embedEndAttr = embedEndPage ? ` embedEndPage="${embedEndPage}"` : '';
        const embedEnabledAttr = embedEnabled ? ` embedEnabled="${embedEnabled}"` : '';
        cml.push(
          `<asset id="${id}" name="${escapedName}" extension="${extension}" assetType="${assetType}"${embedEnabledAttr}${embedStartAttr}${embedEndAttr}/>`
        );
        break;
      }

      case BLOCK_TYPES.TABLE: {
        const table = node as TableElement;
        const hasHeader = !table.headless;
        let numRows = 0;
        let numCols = 0;

        const tableNodes = table.children
          .filter((childNode: TableRowElement) => childNode.type === BLOCK_TYPES.TABLE_ROW)
          .map((childNode, idx) => {
            const tableRowElement = childNode as TableRowElement;
            if (hasHeader && idx === 0) {
              numRows += 1;
              return `<thead>${slateToCml([tableRowElement])}</thead>`;
            }

            numRows += 1;
            numCols = tableRowElement.children.length;
            return slateToCml([tableRowElement]);
          });

        // Copy-pasting tables from currently unsupported sources like Excel sheets can lead
        // to tables with invalid row/col count. e.g. https://sentry.io/organizations/coursera/issues/870202638
        if (numRows > 0 && numCols > 0) {
          const cmlAttributes = [
            ...getCmlAttribute(['rows', `${numRows}`]),
            ...getCmlAttribute(['columns', `${numCols}`]),
          ].join(' ');
          cml.push(`<table ${cmlAttributes}>${tableNodes.join('')}</table>`);
        }

        break;
      }

      case BLOCK_TYPES.TABLE_ROW: {
        cml.push(`<tr>${slateToCml(node.children)}</tr>`);
        break;
      }

      case BLOCK_TYPES.TABLE_CELL: {
        const tag = node.header ? 'th' : 'td';
        cml.push(`<${tag}>${slateToCml(node.children)}</${tag}>`);
        break;
      }

      case BLOCK_TYPES.CODE: {
        const { language, evaluatorId, codeText, name } = node as CodeElement;
        const escapedCodeText = escape(codeText);
        const escapedName = escape(name);

        const cmlAttributes = [
          ...getCmlAttribute(['language', language], !!language),
          ...getCmlAttribute(['evaluatorId', evaluatorId], !!evaluatorId),
          ...getCmlAttribute(['name', escapedName], !!name),
        ].join(' ');

        cml.push(`<code ${cmlAttributes}>${escapedCodeText}</code>`);
        break;
      }

      case BLOCK_TYPES.PERSONALIZATION_TAG: {
        const tagValue = (node as PersonalizationTagElement).tagValue || '';
        cml.push(`<ptag>${tagValue}</ptag>`);
        break;
      }

      case BLOCK_TYPES.MATH_BLOCK: {
        const { formula } = node as MathBlockElement;
        cml.push(`<text${formula ? ' hasMath="true"' : ''}>${escape(formula)}</text>`);
        break;
      }

      case BLOCK_TYPES.WIDGET: {
        const { id } = node as WidgetElement;
        const cmlAttributes = [...getCmlAttribute(['id', id], !!id)].join(' ');

        cml.push(`<widget ${cmlAttributes}/>`);
        break;
      }

      case BLOCK_TYPES.TEXT:
      default: {
        // handle as text content
        const { variant } = node as TextElement;
        const cmlAttributes = [
          ...getCmlAttribute(['hasMath', 'true'], attributes.hasMath),
          ...getCmlAttribute(['variant', variant], !!variant),
        ].join(' ');

        cml.push(`<text${cmlAttributes.trim().length > 0 ? ` ${cmlAttributes}` : ''}>${textCML}</text>`);
        break;
      }
    }
  });

  return cml.join('');
};
